import { simpleMix } from '@entities/simple-mix';
import { warehouseModel } from '@entities/warehouse';
import { formatBagId } from '@lib/bag-formatting';
import { Burger } from '@ui/burger-context-menu';
import { ButtonPrimary, ButtonSecondary } from '@ui/button';
import { ModalDialog } from '@ui/modal-dialog';
import { useStore } from 'effector-react';
import { useNavigate } from 'react-router';
import {
  $removeBagConfirmation,
  emptyBag,
  mixBag,
  removeBagCancel,
  removeBagConfirm,
  removeBag,
  unmixBag,
  $emptyBagConfirmation,
  emptyBagCancel,
  emptyBagConfirm,
} from './model';
import { removeBag as removeBagEvent } from '@/entities/simple-mix/model';
import {
  $agricultureById,
  $copiedBagParamsBuffer,
  copyAgricultureModal,
  copySleeveParams,
  editAgricultureModal,
} from '@@/agriculture/store/agriculture.store';
import { IBag, IFillWarehouseBagData } from '@@/bags/ts';
import { fillWarehouseBag } from '@entities/warehouse/store';

import styles from './styles.module.css';
import { fillWarehouseBagSuccess } from '@entities/warehouse/store';
import { map } from '@widgets/storage-viewer/map';
import { $mixedBags } from '@/entities/simple-mix/model';
import {
  selectSleeve,
  toggleLeftWh,
  toggleRightWh,
} from '@/widgets/storage-viewer/model';
import { DiContainer } from '@/modules/di/di-container';
import { WarehousesService } from '@/modules/warehouses/services/warehouses.service';
import { notification } from 'antd';
import { onSubmit } from '@/modules/bags/functions/on-submit.function';
import { copyBag } from '@/modules/bags/functions/copy-bag.function';
import {
  $isSubscribed,
  switchDisplayBuySubscription,
} from '@/modules/auth/store/auth.store';

const { MAX_SIMPLE_MIXING_ITEMS_CNT } = simpleMix;

export function EditBag({
  bag,
  burgerSize = 'big',
}: {
  bag: IBag;
  burgerSize?: 'small' | 'big';
}) {
  const navigate = useNavigate();
  const removeBagConfirmation = useStore($removeBagConfirmation);
  const emptyBagConfirmation = useStore($emptyBagConfirmation);
  const mixedBagsCount = useStore(simpleMix.$mixedBagsCount);
  const isRemovingBagInProgress = useStore(warehouseModel.$isRemovingBag);
  const isClearingBagInProgress = useStore(warehouseModel.$isEmptyingBag);
  const agricultureById = useStore($agricultureById);
  const isMixed = simpleMix.isBagMixed(bag.id);
  const isEmpty = bag.agricultureId === null;
  const bagIdFormatted = formatBagId(bag.id);
  const selectedWarehouse = useStore(warehouseModel.$warehouseInfo);
  const copiedBagParamsBuffer = useStore($copiedBagParamsBuffer);
  const isSubscribed = useStore($isSubscribed);

  const canMix =
    agricultureById[bag.agricultureId as number]?.mixable &&
    mixedBagsCount < MAX_SIMPLE_MIXING_ITEMS_CNT;

  const onSelect = async ({ value }: { value: string; label: string }) => {
    switch (value) {
      case 'remove':
        removeBag({ warehouseId: bag.warehouseId, bagId: bag.id });
        break;
      case 'empty':
        emptyBag({ warehouseId: bag.warehouseId, bagId: bag.id });
        break;
      case 'edit':
        editAgricultureModal({ id: bag.id });
        break;
      case 'copy':
        copyAgricultureModal({ bagId: bag.id });
        break;
      case 'copy_params':
        copySleeveParams({ bag });
        break;
      case 'paste_params':
        copiedBagParamsBuffer &&
          (await copyBag({
            bag: copiedBagParamsBuffer,
            adressId: bag.id,
            wh: selectedWarehouse,
          }));
        break;
      case 'mix':
        if (isSubscribed === false) return switchDisplayBuySubscription(true);
        mixBag({ bagId: bag.id });
        const state = {
          weight: bag.weight,
          gluten: bag.values.find((item) => item.name === 'Клейковина')?.value,
          idk: bag.values.find((item) => item.name === 'ИДК')?.value,
          bug: 0, // WARNING
          protein: bag.values.find((item) => item.name === 'Протеин')?.value,
          cp: bag.values.find((item) => item.name === 'Число падения')?.value,
          nature: bag.values.find((item) => item.name === 'Натура')?.value,
          humidity: bag.values.find((item) => item.name === 'Влажность культуры')
            ?.value,
        };

        if (localStorage.getItem('L_BAG') === bag.id) {
          selectedWarehouse &&
            toggleLeftWh({
              id: selectedWarehouse.id,
              name: selectedWarehouse.name,
            } as any);
          localStorage.setItem('bagA', JSON.stringify(state));
          localStorage.setItem('bag_a_initial_state', JSON.stringify(state));
        } else if (localStorage.getItem('R_BAG') === bag.id) {
          selectedWarehouse &&
            toggleRightWh({
              id: selectedWarehouse.id,
              name: selectedWarehouse.name,
            } as any);
          //navigate('/mixing-simple');

          localStorage.setItem('bagB', JSON.stringify(state));
          localStorage.setItem('bag_b_initial_state', JSON.stringify(state));
          navigate('/calculator');
        }
        map.markSleeve(bag, true);
        break;
      case 'unmix':
        if (localStorage.getItem('R_BAG') === bag.id) {
          toggleRightWh(null);
          removeBagEvent('right');
          localStorage.removeItem('bag_b_initial_state');
          localStorage.removeItem('bagB');
        } else if (localStorage.getItem('L_BAG') === bag.id) {
          toggleLeftWh(null);
          removeBagEvent('left');
          localStorage.removeItem('bag_a_initial_state');
          localStorage.removeItem('bagA');
        }
        unmixBag({ bagId: bag.id });
        map.markSleeve(bag);
        break;
      default:
        break;
    }
  };

  let menuItems = isEmpty
    ? []
    : [
        ...(!isMixed && canMix ? [{ label: 'замиксовать', value: 'mix' }] : []),
        ...(!isMixed ? [{ label: 'дублировать', value: 'copy' }] : []),
        ...((!isMixed && !copiedBagParamsBuffer) ||
        selectedWarehouse?.id !== copiedBagParamsBuffer?.warehouseId
          ? [{ label: 'копировать параметры', value: 'copy_params' }]
          : []),
        ...(!isMixed ? [{ label: 'редактировать', value: 'edit' }] : []),
        ...(!isMixed ? [{ label: 'опустошить', value: 'empty' }] : []),
        ...(isMixed ? [{ label: 'удалить из бункера', value: 'unmix' }] : []),
      ];

  if (
    copiedBagParamsBuffer &&
    bag.id !== copiedBagParamsBuffer.id &&
    isEmpty &&
    selectedWarehouse?.id === copiedBagParamsBuffer.warehouseId
  ) {
    menuItems.unshift({ label: 'вставить параметры', value: 'paste_params' });
  }
  //Нужно чтобы не выдавал пустой popover
  const disabledEmpty = menuItems.length === 0;
  return (
    <>
      <Burger
        buttonClassName={styles[burgerSize]}
        disabled={isClearingBagInProgress || isRemovingBagInProgress || disabledEmpty}
        onItemSelect={onSelect}
        items={menuItems}
      />

      {removeBagConfirmation[bag.id] && (
        <ModalDialog
          title={`Удалить хранилище ${bagIdFormatted} ?`}
          onClose={() => removeBagCancel()}
          actions={
            <>
              <ButtonPrimary
                disabled={isRemovingBagInProgress}
                label="Удалить"
                onClick={() => removeBagConfirm()}
              />
              <ButtonSecondary label="Отмена" onClick={() => removeBagCancel()} />
            </>
          }
        >
          Отменить действие будет нельзя!
        </ModalDialog>
      )}

      {emptyBagConfirmation[bag.id] && (
        <ModalDialog
          title={`Опустошить хранилище ${bagIdFormatted} ?`}
          onClose={() => emptyBagCancel()}
          actions={
            <>
              <ButtonPrimary
                disabled={isClearingBagInProgress}
                label="Опустошить"
                onClick={() => {
                  emptyBagConfirm();
                  selectSleeve(bag.id);
                }}
              />
              <ButtonSecondary label="Отмена" onClick={() => emptyBagCancel()} />
            </>
          }
        >
          <div className={styles.modal_container}>
            <span className={styles.important_text}>ВАЖНО!</span>
            {burgerSize === 'big' && (
              <span className={styles.content_text}>
                При опустошении рукава, вы используете одну лицензию на рукав. Кол-во
                доступных вам лицензий, уменьшится на 1 (единицу).
              </span>
            )}
            <span className={styles.cancel_action}>
              Отменить действие будет нельзя!
            </span>
          </div>
        </ModalDialog>
      )}
    </>
  );
}
